<template>
  <div
    :class="
      classes(
        n(),
        n(`--${mergedLayout}`),
        {
          [n('--error')]: hasError,
          [n('--focused')]: isFocused,
          [n('--disabled')]: mergedDisabled,
          [n('--borderless')]: !bordered,
        }
      )
    "
    @focusin="handleFocusin"
    @focusout="handleFocusout"
  >
    <!-- 小标题区域 -->
    <div
      v-if="subtitle || $slots.subtitle"
      :class="n('subtitle')"
    >
      <slot name="subtitle">
        {{ subtitle }}
      </slot>
    </div>

    <!-- 主区域容器 -->
    <div :class="n('wrapper')">
      <div :class="n('row')">
        <!-- 标签 -->
        <div
          v-if="label || $slots.label"
          :class="n('label')"
          :style="labelStyle"
        >
          <span
            v-if="required"
            :class="n('required')"
          >*</span>
          <slot name="label">
            {{ label }}
          </slot>
        </div>

        <!-- 控件区域 -->
        <div :class="n('control')">
          <slot />
        </div>
      </div>

      <!-- 错误提示（底线上方） -->
      <div
        v-if="shouldShowError && currentErrorMessage"
        :class="n('error')"
      >
        <slot
          name="error"
          :error-message="currentErrorMessage"
        >
          {{ currentErrorMessage }}
        </slot>
      </div>
    </div>

    <!-- 辅助说明（底线下方） -->
    <div
      v-if="tips || $slots.tips"
      :class="n('tips')"
    >
      <slot name="tips">
        {{ tips }}
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, inject, watch, onMounted, onUnmounted, provide } from 'virtual-vue';
import { formItemProps } from './form-item-props';
import { formItemEmits } from './emits';
import { createNamespace } from '../utils/components';
import { FORM_KEY, FORM_ITEM_KEY } from './constants';
import { validateRules, filterRulesByTrigger } from './use-form-validate';
import type { FormTrigger, FormRule } from './types';

const { name, n, classes } = createNamespace('form-item');

export default defineComponent({
  name,
  props: formItemProps,
  emits: formItemEmits,
  setup(props, { emit, expose }) {
    const formContext = inject(FORM_KEY, null);

    const innerErrorMessage = ref('');
    const isValidating = ref(false);
    const isFocused = ref(false);

    const mergedLayout = computed(() => props.layout || (formContext?.layout) || 'horizontal');

    const mergedDisabled = computed(() => {
      if (props.disabled !== null) return props.disabled;
      return formContext?.disabled || false;
    });

    const mergedLabelWidth = computed(() => props.labelWidth ?? formContext?.labelWidth);

    const mergedLabelAlign = computed(() => props.labelAlign || formContext?.labelAlign || 'left');

    const mergedShowErrorMessage = computed(() => {
      if (props.showErrorMessage !== null) return props.showErrorMessage;
      return formContext?.showErrorMessage ?? true;
    });

    const labelStyle = computed(() => {
      const style: Record<string, string> = {};
      if (mergedLabelWidth.value) {
        style.width = typeof mergedLabelWidth.value === 'number'
          ? `${mergedLabelWidth.value}px`
          : mergedLabelWidth.value;
      }
      if (mergedLabelAlign.value !== 'top') {
        style.textAlign = mergedLabelAlign.value;
      }
      return style;
    });

    const hasError = computed(() => {
      if (props.error !== null) return props.error;
      return !!innerErrorMessage.value;
    });

    const currentErrorMessage = computed(() => {
      if (props.errorMessage) return props.errorMessage;
      return innerErrorMessage.value;
    });

    const shouldShowError = computed(() => mergedShowErrorMessage.value && hasError.value);

    const fieldRules = computed((): FormRule[] => {
      if (props.rules) return props.rules;
      if (formContext?.rules && props.name) {
        return formContext.rules[props.name] || [];
      }
      return [];
    });

    const getFieldValue = () => {
      if (!props.name || !formContext?.model) return undefined;
      return formContext.model[props.name];
    };

    const doValidate = async (trigger?: FormTrigger, options?: { silent?: boolean }): Promise<string[]> => {
      const silent = options?.silent || false;
      const rules = filterRulesByTrigger(fieldRules.value, {
        trigger,
        itemTrigger: props.validateTrigger,
        formTrigger: formContext?.validateTrigger || 'onBlur',
      });

      if (rules.length === 0) return [];

      isValidating.value = true;
      const value = getFieldValue();
      const errors = await validateRules(value, rules, {
        label: props.label,
        validateMessages: formContext?.validateMessages,
      });
      isValidating.value = false;

      if (!silent) {
        innerErrorMessage.value = errors[0] || '';
        emit('validate', {
          name: props.name,
          valid: errors.length === 0,
          errors,
        });
      }

      return errors;
    };

    const validate = async (trigger?: FormTrigger): Promise<string[]> => doValidate(trigger);

    const validateOnly = async (trigger?: FormTrigger): Promise<string[]> => doValidate(trigger, { silent: true });

    const resetValidation = () => {
      innerErrorMessage.value = '';
    };

    const resetField = () => {
      resetValidation();
    };

    const getErrors = (): string[] => (innerErrorMessage.value ? [innerErrorMessage.value] : []);

    if (formContext?.model) {
      watch(
        () => (props.name ? formContext.model[props.name] : undefined),
        () => {
          if (props.name) doValidate('onChange');
        },
      );
    }

    const markFocus = () => {
      isFocused.value = true;
    };

    const markBlur = () => {
      // 幂等：已失焦则跳过，避免 H5 下 context 通道与 DOM focusout 重复触发校验
      if (!isFocused.value) return;
      isFocused.value = false;
      if (props.name) {
        doValidate('onBlur');
      }
    };

    const handleFocusin = () => {
      markFocus();
    };

    const handleFocusout = (e: FocusEvent) => {
      const nextTarget = e.relatedTarget as Node | null;
      if (nextTarget && (e.currentTarget as HTMLElement).contains(nextTarget)) {
        return;
      }
      markBlur();
    };

    const fieldInstance = {
      get name() {
        return props.name || '';
      },
      validate,
      validateOnly,
      resetValidation,
      resetField,
      getErrors,
    };

    onMounted(() => {
      if (props.name && formContext?.registerField) {
        formContext.registerField(fieldInstance);
      }
    });

    watch(
      () => props.name,
      (newName, oldName) => {
        if (!formContext) return;
        if (oldName) {
          formContext.unregisterField(fieldInstance);
        }
        if (newName) {
          formContext.registerField(fieldInstance);
        }
      },
    );

    onUnmounted(() => {
      if (props.name && formContext?.unregisterField) {
        formContext.unregisterField(fieldInstance);
      }
    });

    provide(FORM_ITEM_KEY, {
      inFormItem: true,
      error: hasError,
      disabled: mergedDisabled,
      // LiteApp 等环境原生输入框 focus/blur 不会冒泡成 DOM focusin/focusout，
      // 通过上下文让子控件主动通知聚焦状态
      onControlFocus: markFocus,
      onControlBlur: markBlur,
    });

    const exposed = {
      validate,
      validateOnly,
      resetValidation,
    };

    if (expose) {
      expose(exposed);
    }

    return {
      n,
      classes,
      mergedLayout,
      mergedDisabled,
      isFocused,
      labelStyle,
      hasError,
      currentErrorMessage,
      shouldShowError,
      handleFocusin,
      handleFocusout,
      ...exposed,
    };
  },
});
</script>
